
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Input, WinBaghrafLogo } from '../UI';
import { useTitle, PAGE_TITLES } from '../../hooks';
import { Lock, Mail, ArrowRight, AlertCircle, CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';

interface LoginProps {
  onLoginSuccess?: () => void;
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  // Page title
  useTitle(PAGE_TITLES.LOGIN);

  const { login, isAuthenticated, isLoading: authLoading, error: authError, clearError } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && onLoginSuccess) {
      onLoginSuccess();
    }
  }, [isAuthenticated, onLoginSuccess]);

  // Sync auth context error
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setError('');
    clearError();

    // Basic validation
    if (!email.trim()) {
      setError('Email wajib diisi.');
      return;
    }
    if (!password) {
      setError('Kata sandi wajib diisi.');
      return;
    }

    setIsSubmitting(true);

    try {
      await login(email.trim(), password, remember);
      // onLoginSuccess will be called via the useEffect above
    } catch {
      // Error is already set by the auth context
      setError('Email atau kata sandi tidak valid.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Demo credential filler (only in development)
  const fillCredentials = (demoEmail: string) => {
    setEmail(demoEmail);
    setPassword('admin123');
    setError('');
    clearError();
  };

  const isLoading = isSubmitting || authLoading;

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#0F172A] font-sans p-4 sm:p-6 relative overflow-hidden">
      
      {/* Background Ambience / Lighting Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Top Right Red Glow (Brand Color) */}
        <div className="absolute -top-[20%] -right-[10%] w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-red-900/30 rounded-full blur-3xl mix-blend-screen opacity-40 animate-blob"></div>
        {/* Bottom Left Blue/Slate Glow */}
        <div className="absolute -bottom-[20%] -left-[10%] w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-slate-700/30 rounded-full blur-3xl mix-blend-screen opacity-40 animate-blob animation-delay-2000"></div>
        
        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
      </div>

      {/* Main Card */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl shadow-black/50 border border-slate-700/50 relative z-10 overflow-hidden">
            
            {/* Header Section */}
            <div className="pt-8 sm:pt-10 pb-4 sm:pb-6 px-6 sm:px-8 text-center bg-white relative">
                <div className="flex justify-center mb-4 sm:mb-6 transform hover:scale-105 transition-transform duration-500">
                     <WinBaghrafLogo className="w-20 h-16 sm:w-24 sm:h-20" showText={false} />
                </div>
                <h1 className="text-2xl sm:text-3xl font-serif font-bold tracking-widest text-slate-900 uppercase">WBB Group</h1>
                <div className="flex items-center justify-center gap-2 mt-2">
                    <div className="h-px w-6 sm:w-8 bg-linear-to-r from-transparent to-slate-300"></div>
                    <h3 className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-[0.15em] sm:tracking-[0.2em]">Financial Portal</h3>
                    <div className="h-px w-6 sm:w-8 bg-linear-to-l from-transparent to-slate-300"></div>
                </div>
            </div>

            <div className="px-6 sm:px-8 pb-8 sm:pb-10">
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                    <div className="space-y-4">
                        <Input 
                            label="Email Kerja" 
                            type="email" 
                            startIcon={Mail} 
                            placeholder="nama@wbbgroup.com" 
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              if (error) setError('');
                            }}
                            autoComplete="email"
                            required
                            disabled={isLoading}
                            className="bg-slate-50 border-slate-200 focus:border-red-700 focus:ring-red-700/10"
                        />
                        <div className="relative">
                            <Input 
                                label="Kata Sandi" 
                                type={showPassword ? 'text' : 'password'}
                                startIcon={Lock} 
                                placeholder="••••••••" 
                                value={password}
                                onChange={(e) => {
                                  setPassword(e.target.value);
                                  if (error) setError('');
                                }}
                                autoComplete="current-password"
                                required
                                disabled={isLoading}
                                className="bg-slate-50 border-slate-200 focus:border-red-700 focus:ring-red-700/10 pr-12"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-[38px] text-slate-400 hover:text-slate-600 transition-colors"
                              tabIndex={-1}
                            >
                              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                            <div className="flex items-center justify-between mt-2">
                                 <div className="flex items-center">
                                    <input 
                                      id="remember-me" 
                                      name="remember-me" 
                                      type="checkbox" 
                                      checked={remember}
                                      onChange={(e) => setRemember(e.target.checked)}
                                      disabled={isLoading}
                                      className="h-4 w-4 text-red-700 focus:ring-red-700 border-gray-300 rounded cursor-pointer" 
                                    />
                                    <label htmlFor="remember-me" className="ml-2 block text-xs text-slate-500 cursor-pointer">Ingat perangkat ini</label>
                                 </div>
                                 <div className="text-xs">
                                    <a href="#" className="font-medium text-slate-600 hover:text-red-700 transition-colors">Lupa akses?</a>
                                 </div>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm font-medium border border-red-100 flex items-center animate-fade-in-down">
                            <AlertCircle className="w-4 h-4 mr-2 shrink-0" />
                            {error}
                        </div>
                    )}

                    <button 
                        type="submit" 
                        disabled={isLoading}
                        className="w-full flex items-center justify-center py-3.5 sm:py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed touch-target"
                    >
                        {isLoading ? (
                            <span className="flex items-center">
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Memproses...
                            </span>
                        ) : (
                            <span className="flex items-center">
                                Masuk ke Portal <ArrowRight className="w-4 h-4 ml-2" />
                            </span>
                        )}
                    </button>
                </form>

                {/* Demo Access - ONLY shown in development mode */}
                {import.meta.env.DEV && (
                  <div className="mt-8 pt-6 border-t border-slate-100">
                       {/* Demo Hints */}
                       <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-xs text-slate-600">
                          <div className="flex items-center justify-between mb-3">
                              <p className="font-bold text-slate-800 uppercase tracking-wider flex items-center">
                                  <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-500" /> 
                                  Akses Demo
                              </p>
                              <span className="text-[10px] bg-amber-100 border border-amber-200 px-2 py-0.5 rounded text-amber-700">DEV ONLY</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                              <button 
                                type="button"
                                onClick={() => fillCredentials('admin@wbbgroup.com')} 
                                disabled={isLoading}
                                className="flex flex-col items-center justify-center py-2 px-1 rounded hover:bg-white hover:shadow-md hover:-translate-y-0.5 border border-transparent hover:border-slate-200 transition-all text-center group bg-white/50 disabled:opacity-50"
                              >
                                  <span className="block font-bold text-slate-800 group-hover:text-red-700 transition-colors">Administrator</span>
                              </button>
                              <button 
                                type="button"
                                onClick={() => fillCredentials('stafadmin@wbbgroup.com')} 
                                disabled={isLoading}
                                className="flex flex-col items-center justify-center py-2 px-1 rounded hover:bg-white hover:shadow-md hover:-translate-y-0.5 border border-transparent hover:border-slate-200 transition-all text-center group bg-white/50 disabled:opacity-50"
                              >
                                  <span className="block font-bold text-slate-800 group-hover:text-purple-700 transition-colors">Staf Admin</span>
                              </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                              <button 
                                type="button"
                                onClick={() => fillCredentials('stafho@wbbgroup.com')} 
                                disabled={isLoading}
                                className="flex flex-col items-center justify-center py-2 px-1 rounded hover:bg-white hover:shadow-md hover:-translate-y-0.5 border border-transparent hover:border-slate-200 transition-all text-center group bg-white/50 disabled:opacity-50"
                              >
                                  <span className="block font-bold text-slate-800 group-hover:text-blue-700 transition-colors">Staf HO</span>
                              </button>
                              <button 
                                type="button"
                                onClick={() => fillCredentials('staffinance@wbbgroup.com')} 
                                disabled={isLoading}
                                className="flex flex-col items-center justify-center py-2 px-1 rounded hover:bg-white hover:shadow-md hover:-translate-y-0.5 border border-transparent hover:border-slate-200 transition-all text-center group bg-white/50 disabled:opacity-50"
                              >
                                  <span className="block font-bold text-slate-800 group-hover:text-emerald-700 transition-colors">Staf Finance</span>
                              </button>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-2 text-center">Password: admin123</p>
                       </div>
                  </div>
                )}
            </div>
            
            {/* Bottom Accent Bar */}
            <div className="h-1.5 w-full bg-linear-to-r from-slate-900 via-red-700 to-slate-900"></div>
      </div>
      
      {/* Footer Copyright */}
      <div className="absolute bottom-4 sm:bottom-6 text-center w-full z-10 px-4">
        <p className="text-slate-500 text-[10px] sm:text-xs font-medium">© {new Date().getFullYear()} WBB Group Internal Systems.</p>
      </div>
    </div>
  );
};
